import React, { useState, useEffect } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { useQueryClient } from "@tanstack/react-query";
import { notify } from "utils/notify";
import { useUpdateDeviceParams, useDeviceStatus } from "hooks/useDevices";
import { useAuth } from "context/AuthContext";
import ChangeWifiDialog from "./ChangeWifiDialog";
import { RegionAdminField } from "./device/RegionAdminField";
import { PoolSizeAdminField } from "./device/PoolSizeAdminField";
import {
  POOL_SHAPES,
  boostOptions,
  backwashOptions,
} from "./device/deviceConfig.constants";
import {
  ConfigPage,
  ConfigFields,
  ConfigGrid,
  ConfigField,
  ConfigActions,
  InfoText,
  PoolShapeContainer,
  PoolShapeGrid,
  PoolShapeCard,
  PoolShapeImage,
  PoolShapeName,
  ConnectivitySection,
} from "styles/devices/deviceConfig.styles";

interface DeviceConfigProps {
  serialNumber: string;
  linkId?: string;
}

export const DeviceConfig: React.FC<DeviceConfigProps> = ({
  serialNumber,
  linkId,
}) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";
  const updateDeviceParams = useUpdateDeviceParams();
  const queryClient = useQueryClient();
  const [wifiDialogVisible, setWifiDialogVisible] = useState(false);
  const { data: deviceStatus } = useDeviceStatus(serialNumber);

  const deviceData = queryClient.getQueryData<any>([
    "getDevice",
    serialNumber,
    linkId ?? null,
  ]);
  const shadow = deviceStatus?.response.shadow;

  const [boostDuration, setBoostDuration] = useState(6);
  const [backwashDuration, setBackwashDuration] = useState(2);
  const [poolVolume, setPoolVolume] = useState(2000);
  const [nipperSize, setNipperSize] = useState(0);
  const [loading, setLoading] = useState(false);
  const [poolShape, setPoolShape] = useState("square");

  useEffect(() => {
    if (!shadow) return;

    setBoostDuration(shadow.setBoostDuration || 6);
    setBackwashDuration(
      shadow.setBackwashDuration ? shadow.setBackwashDuration / 60 : 2
    );
    if (deviceData?.poolSize) setPoolVolume(deviceData.poolSize);
    if (deviceData?.nipperCell) setNipperSize(deviceData.nipperCell);

    const saved = localStorage.getItem(`poolShape_${serialNumber}`);
    if (saved) setPoolShape(saved);
  }, [deviceData, serialNumber, shadow]);

  const selectPoolShape = (shape: string) => {
    setPoolShape(shape);
    localStorage.setItem(`poolShape_${serialNumber}`, shape);
  };

  const saveConfiguration = async () => {
    setLoading(true);

    try {
      if (!shadow) {
        notify.error("Device data not available");
        setLoading(false);
        return;
      }

      const targets = {
        ph: shadow?.phSetpoint || 0,
        orp: shadow?.orpSetpoint || 0,
        temp: shadow?.tempSetpoint || 0,
        salinity: shadow?.salinitySetpoint || 0,
        vsdPumpSpeed: shadow.vsdSpeed,
        cell: shadow?.cellOutput || 0,
        isVsdPumpConnected: shadow.vsdIsConnected || false,
        phConnected: shadow?.phConnected || false,
        orpConnected: shadow?.orpConnected || false,
        salinityConnected: shadow?.salinityConnected || false,
        tempConnected: shadow?.tempConnected || false,
        boostDuration,
        backwashDuration: backwashDuration * 60,
      };

      updateDeviceParams.mutate(
        { serialNumber, targets },
        {
          onSuccess: (response) => {
            queryClient.setQueryData(
              ["getDevice", serialNumber],
              (oldData: any) => {
                if (!oldData) return oldData;
                const newData = JSON.parse(JSON.stringify(oldData));
                if (response.success && response.data) {
                  newData.boostDuration = boostDuration;
                  newData.backwashDuration = backwashDuration * 60;
                }
                return newData;
              }
            );
            notify.success("Device configuration saved successfully");
          },
          onError: (error) => {
            console.error("Failed to update configuration:", error);
            notify.error("Failed to save device configuration");
          },
          onSettled: () => setLoading(false),
        }
      );
    } catch (error) {
      console.error("Error saving device configuration:", error);
      notify.error("Failed to save device configuration");
      setLoading(false);
    }
  };

  return (
    <ConfigPage>
      <Card title="Device Configuration">
        <ConfigFields>
          <ConfigGrid>
            <ConfigField>
              <label htmlFor="boostDuration">Boost Duration</label>
              <Dropdown
                id="boostDuration"
                value={boostDuration}
                options={boostOptions}
                onChange={(e) => setBoostDuration(e.value)}
                disabled={loading}
              />
              <small>Set the duration for boost mode operation (6-24 hours)</small>
            </ConfigField>

            <ConfigField>
              <label htmlFor="backwashDuration">Backwash Duration</label>
              <Dropdown
                id="backwashDuration"
                value={backwashDuration}
                options={backwashOptions}
                onChange={(e) => setBackwashDuration(e.value)}
                disabled={loading}
              />
              <small>Set the duration for backwash operation (2-5 minutes)</small>
            </ConfigField>

            <ConfigField>
              <label htmlFor="nipperSize">Nipper Size</label>
              <InfoText>{nipperSize} grams</InfoText>
              <small>Nipper size configuration (read-only)</small>
            </ConfigField>

            {isAdmin ? (
              <PoolSizeAdminField
                serialNumber={serialNumber}
                linkId={linkId}
                currentPoolSize={poolVolume}
              />
            ) : (
              <ConfigField>
                <label htmlFor="poolVolume">Pool Volume</label>
                <InfoText>
                  {poolVolume
                    ? `${poolVolume.toLocaleString()} L`
                    : "Not configured"}
                </InfoText>
                <small>Pool volume in liters (read-only)</small>
              </ConfigField>
            )}
          </ConfigGrid>

          <ConfigActions>
            <Button
              label="Save Configuration"
              icon="pi pi-save"
              onClick={saveConfiguration}
              loading={loading}
              disabled={loading}
            />
          </ConfigActions>

          {isAdmin && (
            <RegionAdminField
              serialNumber={serialNumber}
              linkId={linkId}
              currentRegion={deviceData?.region}
            />
          )}
        </ConfigFields>
      </Card>

      <Card title="Pool Shape">
        <PoolShapeContainer>
          <PoolShapeGrid>
            {POOL_SHAPES.map((shape) => (
              <PoolShapeCard
                key={shape.id}
                $selected={poolShape === shape.id}
                onClick={() => selectPoolShape(shape.id)}
              >
                <PoolShapeImage src={shape.img} alt={shape.alt} />
                <PoolShapeName>{shape.label}</PoolShapeName>
              </PoolShapeCard>
            ))}
          </PoolShapeGrid>
        </PoolShapeContainer>
      </Card>

      <Card title="Network Connectivity">
        <ConnectivitySection>
          <p>Change WiFi on the device using Bluetooth Low Energy.</p>
          <Button
            label="Configure WiFi via BLE"
            icon="pi pi-wifi"
            severity="secondary"
            onClick={() => setWifiDialogVisible(true)}
          />
          <small>
            Make sure the device is in pairing mode and nearby.
          </small>
        </ConnectivitySection>
      </Card>

      <ChangeWifiDialog
        visible={wifiDialogVisible}
        onHide={() => setWifiDialogVisible(false)}
        deviceName={serialNumber}
      />
    </ConfigPage>
  );
};
